export type Permission =
  | 'system.admin'
  | 'system.read'
  | 'system.write'
  | 'system.security'
  | 'system.provision'
  | 'system.topology'
  | 'system.vault.read'
  | 'system.vault.reset'
  | 'system.owner-key.rotate'
  | 'system.members'
  | 'system.agents'
  | 'system.plugins'
  | 'system.commands'
  | 'system.logs'
  | 'user.self'
  | 'room.admin'
  | 'room.read'
  | 'room.write'
  | 'moderation.mute'
  | 'moderation.ban'
  | 'plugin.execute'
  | 'channel.read'
  | 'channel.write'
  | '*';

export type SystemRole =
  | 'owner'
  | 'systemAdmin'
  | 'roomManager'
  | 'moderator'
  | 'member'
  | 'guest'
  | 'serviceDevice';

export type PermissionScope =
  | { kind: 'system' }
  | { kind: 'room'; roomId: string }
  | { kind: 'plugin'; pluginId: string; roomId?: string }
  | { kind: 'channel'; channelId: string; roomId?: string };

export function formatScopeKey(scope: PermissionScope): string {
  switch (scope.kind) {
    case 'system':
      return 'system';
    case 'room':
      return `room:${scope.roomId}`;
    case 'plugin':
      return scope.roomId
        ? `room:${scope.roomId}:plugin:${scope.pluginId}`
        : `plugin:${scope.pluginId}`;
    case 'channel':
      return scope.roomId
        ? `room:${scope.roomId}:channel:${scope.channelId}`
        : `channel:${scope.channelId}`;
  }
}

/**
 * Pure RFC 8785 JSON Canonicalization Scheme (JCS) Serializer.
 * Guarantees deterministic string output across runtimes and platforms.
 */
export function canonicalize(val: unknown): string {
  if (val === null) return 'null';

  const valType = typeof val;

  if (valType === 'number') {
    if (!Number.isFinite(val as number)) {
      throw new Error(`JCS Serialization Error: non-finite number ${val}`);
    }
    return JSON.stringify(val);
  }

  if (valType === 'boolean' || valType === 'string') {
    return JSON.stringify(val);
  }

  if (valType === 'undefined' || valType === 'function' || valType === 'symbol') {
    throw new Error(`JCS Serialization Error: unsupported primitive type ${valType}`);
  }

  if (Array.isArray(val)) {
    const items = val.map((item) => canonicalize(item));
    return `[${items.join(',')}]`;
  }

  if (valType === 'object') {
    const obj = val as Record<string, unknown>;
    const keys = Object.keys(obj).sort(); // Deterministic UTF-16 code unit order
    const members: string[] = [];

    for (const key of keys) {
      const memberVal = obj[key];
      if (memberVal === undefined || typeof memberVal === 'function' || typeof memberVal === 'symbol') {
        continue; // Omit undefined / functions / symbols in object fields per JCS
      }
      members.push(`${JSON.stringify(key)}:${canonicalize(memberVal)}`);
    }

    return `{${members.join(',')}}`;
  }

  throw new Error(`JCS Serialization Error: unhandled value ${String(val)}`);
}

export type ControlAction =
  | 'ASSIGN_ROLE'
  | 'UNASSIGN_ROLE'
  | 'GRANT_PERMISSION'
  | 'REVOKE_PERMISSION'
  | 'APPLY_RESTRICTION'
  | 'LIFT_RESTRICTION'
  | 'OVERRIDE_RESTRICTION';

export interface SignedControlPayload {
  domain: 'mesh-control-plane/v1';
  meshId: string;
  actorId: string;
  keyId: string;
  nonce: string;
  createdAt: number;
  expiresAt: number;
  expectedPolicyVersion: number;
  action: ControlAction;
  targetSubjectId: string;
  scope: PermissionScope;
  role?: SystemRole;
  permission?: Permission;
  restrictionId?: string;
  reason?: string;
}

export interface ControlEnvelope<T = SignedControlPayload> {
  payload: T;
  signature: string;
}

export interface ReplayRecord {
  meshId: string;
  actorId: string;
  keyId: string;
  nonce: string;
  expiresAt: number;
  acceptedAt: number;
}

export interface AccessRequest {
  meshId: string;
  subjectId: string;
  permission: Permission;
  scope: PermissionScope;
  nowMs: number;
}

export interface PolicyChange {
  meshId: string;
  action: ControlAction;
  subjectId: string;
  role?: SystemRole;
  permission?: Permission;
  scope: PermissionScope;
  restrictionId?: string;
  expiresAt?: number;
  reason: string;
}

export type EvaluationReason =
  | 'UNAUTHENTICATED'
  | 'REVOKED'
  | 'EXPLICIT_DENY'
  | 'EXPLICIT_ALLOW'
  | 'ROLE_ALLOW'
  | 'DEFAULT_DENY'
  | 'SCOPE_CONTAINMENT_VIOLATION'
  | 'PRIVILEGE_ESCALATION_DENIED';

export interface EvaluationResult {
  allowed: boolean;
  reason: EvaluationReason;
  evaluatedAtStep: number;
  policyVersion: number;
  triggeringEnvelopeId?: string;
}

export interface DiagnosticTraceStep {
  step: number;
  name: string;
  decision: 'ALLOW' | 'DENY' | 'NEUTRAL';
  details: string;
}

export interface AccessExplanation {
  allowed: boolean;
  reason: EvaluationReason;
  policyVersion: number;
  trace: DiagnosticTraceStep[];
}

export interface AccessRestriction {
  id: string;
  meshId: string;
  subjectId: string;
  permission: Permission;
  scopeKey: string;
  status: 'active' | 'lifted' | 'overridden';
  createdAt: number;
  expiresAt: number;
  reason: string;
  envelopeId: string;
  resolvedAt?: number;
  resolvedBy?: string;
}

export interface PermissionGrant {
  subjectId: string;
  permission: Permission;
  scopeKey: string;
  effect: 'allow' | 'deny';
  expiresAt: number;
  envelopeId: string;
}

export interface PublicKeyResolver {
  resolvePublicKey(actorId: string, keyId: string): Uint8Array | null;
  isKeyRevoked(actorId: string, nowMs: number): boolean;
}

export interface SignatureVerifier {
  verifySignature(actorId: string, keyId: string, canonicalData: string, signature: string): boolean;
}

export interface VerifiedPrincipal {
  principalId: string;
  meshId: string;
  keyId: string;
  authenticated: boolean;
  roles: readonly SystemRole[];
}

export interface DomainTabAccess {
  canView: boolean;
  canEdit: boolean;
  canExecute: boolean;
}

export type CommandResult<T = unknown> =
  | { ok: true; policyVersion: number; data?: T }
  | {
      ok: false;
      code: 'UNAUTHENTICATED' | 'FORBIDDEN' | 'STALE_POLICY' | 'REVOKED';
      explanation: AccessExplanation;
    };

export interface SettingsAccess {
  canEditAccount: boolean;
  canManageMembers: boolean;
  canManageTopology: boolean;
  canManageAgents: boolean;
  canManageVault: boolean;
  canManagePlugins: boolean;
  canManageSecurity: boolean;
  canManageCommands: boolean;
  canViewAuditLogs: boolean;

  identity: DomainTabAccess;
  members: DomainTabAccess;
  topology: DomainTabAccess;
  compute: DomainTabAccess;
  vault: DomainTabAccess;
  modules: DomainTabAccess;
  security: DomainTabAccess;
  commands: DomainTabAccess;
  observability: DomainTabAccess;
}

export function deriveSettingsAccess(
  evaluator: { evaluateAccess: (req: AccessRequest) => EvaluationResult },
  principal: VerifiedPrincipal,
  nowMs: number
): SettingsAccess {
  const emptyTabAccess: DomainTabAccess = { canView: false, canEdit: false, canExecute: false };

  if (!principal.authenticated) {
    return {
      canEditAccount: false,
      canManageMembers: false,
      canManageTopology: false,
      canManageAgents: false,
      canManageVault: false,
      canManagePlugins: false,
      canManageSecurity: false,
      canManageCommands: false,
      canViewAuditLogs: false,
      identity: emptyTabAccess,
      members: emptyTabAccess,
      topology: emptyTabAccess,
      compute: emptyTabAccess,
      vault: emptyTabAccess,
      modules: emptyTabAccess,
      security: emptyTabAccess,
      commands: emptyTabAccess,
      observability: emptyTabAccess,
    };
  }

  const check = (permission: Permission, scopeKey = 'system') => {
    const scope: PermissionScope = scopeKey === 'system' ? { kind: 'system' } : { kind: 'room', roomId: scopeKey };
    return evaluator.evaluateAccess({
      meshId: principal.meshId,
      subjectId: principal.principalId,
      permission,
      scope,
      nowMs,
    }).allowed;
  };

  const canEditAccount = check('user.self' as Permission, `subject:${principal.principalId}`) || check('channel.write' as Permission, `subject:${principal.principalId}`);
  const canManageMembers = check('system.members' as Permission) || check('system.admin' as Permission);
  const canManageTopology = check('system.topology' as Permission) || check('system.admin' as Permission);
  const canManageAgents = check('system.agents' as Permission) || check('system.admin' as Permission);
  const canManageVault = check('system.vault.read' as Permission) || check('system.admin' as Permission);
  const canManagePlugins = check('system.plugins' as Permission) || check('system.admin' as Permission);
  const canManageSecurity = check('system.security' as Permission);
  const canManageCommands = check('system.commands' as Permission) || check('system.admin' as Permission);
  const canViewAuditLogs = check('system.logs' as Permission) || check('system.admin' as Permission);

  return {
    canEditAccount,
    canManageMembers,
    canManageTopology,
    canManageAgents,
    canManageVault,
    canManagePlugins,
    canManageSecurity,
    canManageCommands,
    canViewAuditLogs,

    identity: { canView: true, canEdit: canEditAccount, canExecute: canEditAccount },
    members: { canView: true, canEdit: canManageMembers, canExecute: canManageMembers },
    topology: { canView: true, canEdit: canManageTopology, canExecute: canManageTopology },
    compute: { canView: true, canEdit: canManageAgents, canExecute: canManageAgents },
    vault: { canView: true, canEdit: canManageVault, canExecute: canManageVault },
    modules: { canView: true, canEdit: canManagePlugins, canExecute: canManagePlugins },
    security: { canView: canManageSecurity, canEdit: canManageSecurity, canExecute: canManageSecurity },
    commands: { canView: true, canEdit: canManageCommands, canExecute: canManageCommands },
    observability: { canView: true, canEdit: canViewAuditLogs, canExecute: canViewAuditLogs },
  };
}

export const COMPILED_ROLE_PERMISSIONS: ReadonlyMap<SystemRole, ReadonlySet<Permission>> = new Map<
  SystemRole,
  ReadonlySet<Permission>
>([
  ['owner', new Set<Permission>(['*'])],
  [
    'systemAdmin',
    new Set<Permission>([
      'system.admin',
      'system.read',
      'system.write',
      'system.provision',
      'system.topology',
      'system.vault.read',
      'system.members',
      'system.agents',
      'system.plugins',
      'system.commands',
      'system.logs',
      'room.admin',
      'room.read',
      'room.write',
      'moderation.mute',
      'moderation.ban',
      'plugin.execute',
      'channel.read',
      'channel.write',
    ]),
  ],
  [
    'roomManager',
    new Set<Permission>([
      'room.admin',
      'room.read',
      'room.write',
      'moderation.mute',
      'moderation.ban',
      'plugin.execute',
      'channel.read',
      'channel.write',
    ]),
  ],
  [
    'moderator',
    new Set<Permission>([
      'room.read',
      'moderation.mute',
      'moderation.ban',
      'channel.read',
      'channel.write',
    ]),
  ],
  [
    'member',
    new Set<Permission>([
      'user.self',
      'room.read',
      'room.write',
      'plugin.execute',
      'channel.read',
      'channel.write',
    ]),
  ],
  ['guest', new Set<Permission>(['user.self', 'room.read', 'channel.read'])],
  ['serviceDevice', new Set<Permission>(['plugin.execute', 'channel.read', 'channel.write'])],
]);
